<template>
    <client-only>
		<Teleport :to="highestOrder ? '.highest-portal' : '.popup-portal'" :order="order">
			<transition name="popup-transition" :enter-active-class="openTransitionClass" :leave-active-class="closeTransitionClass" appear>
				<div v-if="isActive" class="popup" ref="content" @click.self="handleOverlayClick">
					<slot/>
				</div>
			</transition>
		</Teleport>
	</client-only>
</template>

<script>
import {disableWindowScroll, enableWindowScroll, shakeElement} from "@spoferan/spoferan-ts-core";
import Toggleable from "../../mixins/Toggleable";

export default {
    name: 'Popup',

    mixins: [Toggleable],

	emits: ['closed'],

    props: {

        // Whether the popup shall be put as highest z-index position if multiple popups and popovers are displayed at the same time
        highestOrder: {
            type: Boolean,
            default: false
        },

        // The order number of the popup if multiple popups are displayed at the same time
        // Is equal to a CSS 'z-index'
        order: {
            type: Number,
            default: null
        },

        // Whether to not allow a closing of the popup by clicking on the overlay background
        persistent: {
            type: Boolean,
            default: false
        },

        // Whether a zoom transition shall be used for opening the popup and for closing via the overlay background
        zoomTransition: {
            type: Boolean,
            default: false
        }
    },

    computed: {
        openTransitionClass() {
            return this.zoomTransition ? 'popup-transition-enter-active-zoom' : 'popup-transition-enter-active';
        },

        closeTransitionClass() {
            return this.zoomTransition ? 'popup-transition-leave-active-zoom' : 'popup-transition-leave-active';
        }
    },

    watch: {
        isActive(active) {
            if (active) {
                disableWindowScroll();
            } else {
                enableWindowScroll();
            }
        }
    },

    beforeUnmount() {
        enableWindowScroll();
        this.isActive = false;
    },

    methods: {
        handleOverlayClick() {
            if (this.persistent) {
                shakeElement(this.$refs.content);
            } else {
                this.close();
            }
        },

        close() {
            this.isActive = false
            this.$emit('closed');
        }
    }
}
</script>

<style>
.popup {
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1001;
  width: calc(100% + 20px);
  max-width: calc(100% + 20px);
  left: -10px;
  right: -10px;
  height: 100%;
  max-height: 100%;
}
.popup > * {
  z-index: 1;
}

.highest-portal .popup {
  z-index: 2003;
}

.popup-transition-enter-active {
  animation: popupBackgroundFadeIn 0.4s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
}
.popup-transition-enter-active > * {
  animation: popupContentScaleInFromBottom 0.4s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
}
.popup-transition-leave-active {
  animation: popupBackgroundFadeOut 0.4s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
}
.popup-transition-leave-active > * {
  animation: popupContentScaleOutToBottom 0.4s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
}
.popup-transition-enter-active-zoom {
  animation: popupBackgroundFadeIn 0.3s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
}
.popup-transition-enter-active-zoom > * {
  animation: popupContentZoomIn 0.3s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
}
.popup-transition-leave-active-zoom {
  animation: popupBackgroundFadeOut 0.3s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
}
.popup-transition-leave-active-zoom > * {
  animation: popupContentZoomOut 0.3s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
}

@keyframes popupBackgroundFadeIn {
  0% {
    background: rgba(0, 0, 0, 0);
  }
  100% {
    background: rgba(0, 0, 0, 0.5);
  }
}
@keyframes popupBackgroundFadeOut {
  0% {
    background: rgba(0, 0, 0, 0.5);
  }
  100% {
    background: rgba(0, 0, 0, 0);
  }
}
@keyframes popupContentScaleInFromBottom {
  0% {
    transform: translateY(1000px);
    opacity: 0;
  }
  100% {
    transform: translateY(0px);
    opacity: 1;
  }
}
@keyframes popupContentScaleOutToBottom {
  0% {
    transform: translateY(0px);
    opacity: 1;
  }
  100% {
    transform: translateY(1000px);
    opacity: 0;
  }
}
@keyframes popupContentZoomIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
@keyframes popupContentZoomOut {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(0);
    opacity: 0;
  }
}
</style>